import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    ListTree,
    Trophy,
    Sparkles,
    LogOut,
    RefreshCw,
    Database,
    PanelLeftClose,
    PanelLeftOpen,
    AlertTriangle,
    Trash2,
    X,
    Sprout,
} from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { Logo } from "../components/shared/Logo";
import { getSeedCount, wipeSeeds, reseedDemo } from "./adminApi";

const NAV = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/responses", label: "Responses", icon: ListTree },
    { to: "/admin/referrers", label: "Top Referrers", icon: Trophy },
    { to: "/admin/analysis", label: "AI Analysis", icon: Sparkles },
];

const SEED_KEY = "klareet_admin_include_seed";

export function useIncludeSeed() {
    const [value, setValue] = useState(() => {
        try {
            return localStorage.getItem(SEED_KEY) === "true";
        } catch {
            return false;
        }
    });
    const update = (next) => {
        setValue(next);
        try {
            localStorage.setItem(SEED_KEY, String(next));
        } catch {
            // ignore
        }
    };
    return [value, update];
}

export function AdminLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { token, admin, verifying, logout } = useAdminAuth();
    const [collapsed, setCollapsed] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [includeSeed, setIncludeSeed] = useIncludeSeed();
    const [wipeOpen, setWipeOpen] = useState(false);
    const [reseedOpen, setReseedOpen] = useState(false);
    const [seedCount, setSeedCount] = useState(null);

    useEffect(() => {
        if (!verifying && !token) {
            navigate("/admin/login", { replace: true });
        }
    }, [verifying, token, navigate]);

    // Keep an up-to-date seed count so the Wipe / Re-seed buttons can switch.
    useEffect(() => {
        let alive = true;
        if (!token) return undefined;
        getSeedCount()
            .then((d) => alive && setSeedCount(d?.count ?? 0))
            .catch(() => alive && setSeedCount(null));
        return () => {
            alive = false;
        };
    }, [token, refreshKey]);

    if (verifying) {
        return (
            <div className="min-h-screen grid place-items-center bg-klareet-NAVY">
                <div className="inline-block w-8 h-8 rounded-full border-2 border-klareet-bor border-t-klareet-T animate-spin" />
            </div>
        );
    }

    if (!token) return null;

    const pageTitle =
        NAV.find((n) => location.pathname.startsWith(n.to))?.label || "Admin";

    return (
        <div className="min-h-screen bg-klareet-NAVY text-klareet-TEXT flex">
            <aside
                className={`hidden md:flex flex-col border-r border-klareet-bor bg-klareet-NAVY transition-all duration-200 ${
                    collapsed ? "w-[72px]" : "w-[260px]"
                }`}
                data-testid="admin-sidebar"
            >
                <div className="h-20 px-5 flex items-center gap-3 border-b border-klareet-bor">
                    {!collapsed ? (
                        <Link to="/admin/dashboard">
                            <Logo className="h-9 w-auto" />
                        </Link>
                    ) : (
                        <Logo variant="icon" className="h-9 w-auto" />
                    )}
                    <button
                        type="button"
                        onClick={() => setCollapsed((c) => !c)}
                        className="ml-auto inline-flex w-9 h-9 rounded-full border border-klareet-bor items-center justify-center text-klareet-MUT hover:text-klareet-T2 hover:border-klareet-T transition-colors"
                        aria-label="Toggle sidebar"
                        data-testid="sidebar-toggle"
                    >
                        {collapsed ? (
                            <PanelLeftOpen size={16} />
                        ) : (
                            <PanelLeftClose size={16} />
                        )}
                    </button>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {NAV.map(({ to, label, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                                    isActive
                                        ? "bg-klareet-T/15 text-klareet-T2 border border-klareet-T/30"
                                        : "text-klareet-MUT hover:text-klareet-TEXT hover:bg-klareet-card border border-transparent"
                                }`
                            }
                        >
                            <Icon size={18} className="shrink-0" />
                            {!collapsed && <span>{label}</span>}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-klareet-bor">
                    {!collapsed && (
                        <div
                            className="px-3 py-2 text-xs font-mono text-klareet-MUT truncate"
                            data-testid="admin-email-display"
                        >
                            {admin?.email || ""}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            logout();
                            navigate("/admin/login", { replace: true });
                        }}
                        className="mt-1 w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-klareet-bor text-klareet-MUT hover:text-klareet-RED hover:border-klareet-RED transition-colors text-sm"
                        data-testid="logout-btn"
                    >
                        <LogOut size={16} />
                        {!collapsed && <span>Sign out</span>}
                    </button>
                </div>
            </aside>

            <div className="flex-1 min-w-0 flex flex-col">
                <header className="h-20 px-5 md:px-8 border-b border-klareet-bor flex items-center justify-between bg-klareet-NAVY/85 backdrop-blur sticky top-0 z-30">
                    <div className="md:hidden">
                        <Logo className="h-9 w-auto" />
                    </div>
                    <h1
                        className="hidden md:block text-xl font-semibold tracking-tight"
                        data-testid="page-title"
                    >
                        {pageTitle}
                    </h1>
                    <div className="flex items-center gap-2">
                        <label
                            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-klareet-bor bg-klareet-card text-xs cursor-pointer select-none"
                            data-testid="include-seed-toggle"
                        >
                            <Database size={14} className="text-klareet-MUT" />
                            <span className="text-klareet-MUT">Include seed data</span>
                            <input
                                type="checkbox"
                                checked={includeSeed}
                                onChange={(e) => setIncludeSeed(e.target.checked)}
                                className="ml-1 accent-klareet-T2"
                            />
                        </label>
                        <button
                            type="button"
                            onClick={() => setWipeOpen(true)}
                            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-klareet-RED/40 bg-klareet-RED/5 text-xs text-klareet-RED hover:bg-klareet-RED/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="wipe-seeds-btn"
                            title="Permanently delete all seed responses"
                            disabled={seedCount === 0}
                        >
                            <AlertTriangle size={14} />
                            Wipe seed data
                        </button>
                        {seedCount === 0 ? (
                            <button
                                type="button"
                                onClick={() => setReseedOpen(true)}
                                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-klareet-T/40 bg-klareet-T/5 text-xs text-klareet-T2 hover:bg-klareet-T/10 transition-colors"
                                data-testid="reseed-btn"
                                title="Re-insert the 20 demo responses"
                            >
                                <Sprout size={14} />
                                Re-seed demo data
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => setRefreshKey((k) => k + 1)}
                            className="inline-flex w-10 h-10 rounded-full border border-klareet-bor items-center justify-center text-klareet-MUT hover:text-klareet-T2 hover:border-klareet-T transition-colors"
                            aria-label="Refresh data"
                            data-testid="refresh-btn"
                        >
                            <RefreshCw size={16} />
                        </button>
                    </div>
                </header>
                <main className="flex-1 px-5 md:px-8 py-8">
                    <Outlet context={{ includeSeed, refreshKey }} />
                </main>
            </div>

            {wipeOpen ? (
                <WipeSeedsModal
                    onCancel={() => setWipeOpen(false)}
                    onWiped={() => {
                        setWipeOpen(false);
                        setRefreshKey((k) => k + 1);
                    }}
                />
            ) : null}

            {reseedOpen ? (
                <ReseedModal
                    onCancel={() => setReseedOpen(false)}
                    onReseeded={() => {
                        setReseedOpen(false);
                        setRefreshKey((k) => k + 1);
                    }}
                />
            ) : null}
        </div>
    );
}

function WipeSeedsModal({ onCancel, onWiped }) {
    const [typed, setTyped] = useState("");
    const [seedCount, setSeedCount] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [done, setDone] = useState(null);

    useEffect(() => {
        let alive = true;
        getSeedCount()
            .then((d) => alive && setSeedCount(d.count ?? 0))
            .catch(() => alive && setSeedCount(0));
        return () => {
            alive = false;
        };
    }, []);

    async function confirm() {
        if (typed !== "WIPE") return;
        setBusy(true);
        setErr(null);
        try {
            const res = await wipeSeeds();
            setDone(res);
            setTimeout(() => onWiped?.(), 900);
        } catch (e) {
            setErr(
                e?.response?.data?.detail || e?.message || "Wipe failed.",
            );
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="fixed inset-0 z-[80] grid place-items-center px-5 bg-klareet-NAVY/85 backdrop-blur-sm">
            <div
                className="max-w-md w-full rounded-3xl border border-klareet-RED/40 bg-klareet-card p-6"
                data-testid="wipe-seeds-modal"
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold tracking-tight text-klareet-TEXT flex items-center gap-2">
                        <AlertTriangle size={18} className="text-klareet-RED" />
                        Wipe seed data
                    </h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="text-klareet-MUT hover:text-klareet-TEXT disabled:opacity-50"
                        data-testid="wipe-seeds-cancel-x"
                    >
                        <X size={18} />
                    </button>
                </div>
                {done ? (
                    <div className="mt-4 p-4 rounded-2xl border border-klareet-GREEN/40 bg-klareet-GREEN/10 text-klareet-GREEN text-sm">
                        Wiped {done.deleted} seed responses. Refreshing…
                    </div>
                ) : (
                    <>
                        <p className="mt-3 text-sm text-klareet-MUT leading-relaxed">
                            Permanently delete{" "}
                            <span
                                className="font-mono text-klareet-RED"
                                data-testid="wipe-seeds-count"
                            >
                                {seedCount === null ? "…" : seedCount}
                            </span>{" "}
                            seed responses? This is irreversible. Type{" "}
                            <span className="font-mono text-klareet-RED">
                                WIPE
                            </span>{" "}
                            to confirm.
                        </p>
                        <input
                            autoFocus
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder="WIPE"
                            className="mt-4 w-full px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-NAVY font-mono text-klareet-TEXT outline-none focus:border-klareet-RED focus:ring-2 focus:ring-klareet-RED/40"
                            data-testid="wipe-seeds-confirm-input"
                            disabled={busy}
                        />
                        {err ? (
                            <div className="mt-2 text-sm text-klareet-RED font-mono">
                                {err}
                            </div>
                        ) : null}
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onCancel}
                                disabled={busy}
                                className="inline-flex items-center px-4 py-2 rounded-full border border-klareet-bor text-klareet-MUT hover:text-klareet-TEXT text-sm disabled:opacity-50"
                                data-testid="wipe-seeds-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirm}
                                disabled={typed !== "WIPE" || busy || seedCount === 0}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-klareet-RED text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                                data-testid="wipe-seeds-confirm-btn"
                            >
                                <Trash2 size={14} />
                                {busy ? "Wiping…" : "Wipe forever"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function ReseedModal({ onCancel, onReseeded }) {
    const [typed, setTyped] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [done, setDone] = useState(null);

    async function confirm() {
        if (typed !== "RESEED") return;
        setBusy(true);
        setErr(null);
        const res = await reseedDemo();
        setBusy(false);
        if (res.ok) {
            setDone(res.data);
            setTimeout(() => onReseeded?.(), 900);
        } else if (res.alreadyPresent) {
            setErr(
                `Seed data is already present (${res.data?.count ?? "?"} docs). Refresh and try Wipe first.`,
            );
        } else {
            setErr(res.error || "Re-seed failed.");
        }
    }

    return (
        <div className="fixed inset-0 z-[80] grid place-items-center px-5 bg-klareet-NAVY/85 backdrop-blur-sm">
            <div
                className="max-w-md w-full rounded-3xl border border-klareet-T/40 bg-klareet-card p-6"
                data-testid="reseed-modal"
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold tracking-tight text-klareet-TEXT flex items-center gap-2">
                        <Sprout size={18} className="text-klareet-T2" />
                        Re-seed demo data
                    </h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="text-klareet-MUT hover:text-klareet-TEXT disabled:opacity-50"
                        data-testid="reseed-cancel-x"
                    >
                        <X size={18} />
                    </button>
                </div>
                {done ? (
                    <div className="mt-4 p-4 rounded-2xl border border-klareet-GREEN/40 bg-klareet-GREEN/10 text-klareet-GREEN text-sm">
                        Re-seeded {done.inserted} demo responses. Refreshing…
                    </div>
                ) : (
                    <>
                        <p className="mt-3 text-sm text-klareet-MUT leading-relaxed">
                            Insert the canonical{" "}
                            <span className="font-mono text-klareet-T2">
                                20
                            </span>{" "}
                            demo responses (spanning all 4 personas, varied
                            pains/prices/waitlist intents). Type{" "}
                            <span className="font-mono text-klareet-T2">
                                RESEED
                            </span>{" "}
                            to confirm.
                        </p>
                        <input
                            autoFocus
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder="RESEED"
                            className="mt-4 w-full px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-NAVY font-mono text-klareet-TEXT outline-none focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40"
                            data-testid="reseed-confirm-input"
                            disabled={busy}
                        />
                        {err ? (
                            <div className="mt-2 text-sm text-klareet-RED font-mono">
                                {err}
                            </div>
                        ) : null}
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={onCancel}
                                disabled={busy}
                                className="inline-flex items-center px-4 py-2 rounded-full border border-klareet-bor text-klareet-MUT hover:text-klareet-TEXT text-sm disabled:opacity-50"
                                data-testid="reseed-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirm}
                                disabled={typed !== "RESEED" || busy}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-klareet-T text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                                data-testid="reseed-confirm-btn"
                            >
                                <Sprout size={14} />
                                {busy ? "Re-seeding…" : "Re-seed now"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

